/* eslint-disable react-hooks/rules-of-hooks */
import { createStandaloneToast } from '@chakra-ui/react';
import axios, { AxiosRequestConfig } from 'axios';
import configs from 'configs';
import { HTTP_CODE_RESPONSE } from 'helpers/constant';
import { ERROR_CODE } from 'helpers/errorcode';
import { getStorage, STORAGE_KEY } from 'helpers/storage';
import { ENDPOINT } from 'services/configs/endpoint.constant';
import { DocumentService } from 'services/document/document.service';
import { authInstance } from 'utils/auth';
import { setStorageReadBlobToken } from 'utils/blob';

const { toast } = createStandaloneToast();

export const urlSSE = `${configs.base.BACKEND_URL}${ENDPOINT.SSE_CONNECTION}`;

const TIMEOUT_IN_MILLISECONDS = 1800000;

const axiosInstance = axios.create({
  baseURL: configs.base.BACKEND_URL,
  timeout: TIMEOUT_IN_MILLISECONDS,
});

const publicAxiosInstance = axios.create({
  baseURL: configs.base.BACKEND_URL,
  timeout: TIMEOUT_IN_MILLISECONDS,
});

export const request = <T = any>(config: AxiosRequestConfig) =>
  axiosInstance.request<any, T>(config);
export const publicRequest = (config: AxiosRequestConfig) => publicAxiosInstance.request(config);

async function refreshTokenSAS() {
  return DocumentService.getReadToken().then((res) => {
    setStorageReadBlobToken(res.token);
  });
}

axiosInstance.interceptors.request.use(
  function (config) {
    const token = getStorage(STORAGE_KEY.TOKEN);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  function (response) {
    return response.data;
  },
  function (error) {
    const { config, response } = error;
    if (
      response?.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
      response?.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN
    ) {
      return refreshTokenSAS()
        .then(() => {
          return axiosInstance(config);
        })
        .catch((e) => {
          console.log(e);
        });
    }
    if (response?.status === HTTP_CODE_RESPONSE.ERROR) {
      toast({
        title: 'Error',
        description: response.data?.error_message
          ? response.data?.error_message
          : 'Something went wrong. Try again',
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
      return {
        message: response.data?.error_message
          ? response.data?.error_message
          : 'Something went wrong. Try again',
        success: false,
      };
    }
    if (response?.status === HTTP_CODE_RESPONSE.BAD_REQUEST) {
      toast({
        title: 'Error',
        description: response.data?.error_message
          ? response.data?.error_message
          : 'Something went wrong. Try again',
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
      return {
        message: response.data?.error_message
          ? response.data?.error_message
          : 'Something went wrong. Try again',
        success: false,
      };
    }
    if (response?.status === HTTP_CODE_RESPONSE.UNAUTHENTICATED) {
      document.cookie = 'a_j=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      authInstance.logout();
      return ERROR_CODE.UNAUTHENTICATED;
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
